// CART SIDEBAR COMPONENT final js  ===========
class BzCartSidebar extends HTMLElement {
  constructor() {
    super();
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEsc = this.handleEsc.bind(this);
    this.handleClearCartConfirmation = this.handleClearCartConfirmation.bind(this);
  }

  connectedCallback() {
    this.drawer = this.querySelector("[data-cart-sidebar]");
    this.closeBtn = this.querySelector("[data-close-cart]");
    this.itemsContainer = this.querySelector("[data-cart-items]");
    this.subtotalEl = this.querySelector("[data-cart-subtotal]");
    this.clearBtn = this.querySelector("[data-clear-cart]");
    this.clearLoader = this.clearBtn?.querySelector("[data-clear-loader]");
    // Elements for the confirmation popup
    this.confirmationPopup = this.querySelector("[data-clear-cart-confirmation]");
    this.confirmBtn = this.confirmationPopup.querySelector("[data-confirm-clear]");
    this.cancelBtn = this.confirmationPopup.querySelector("[data-cancel-clear]");
    this.isDrawer = !!this.drawer?.classList.contains("cart-sidebar");
    if (this.isDrawer) {
      this.drawer.setAttribute("role", "dialog");
      this.drawer.setAttribute("aria-modal", "true");
      const title = this.drawer.querySelector(".cart-header h2");
      if (title) {
        if (!title.id) title.id = "CartSidebarTitle";
        this.drawer.setAttribute("aria-labelledby", title.id);
      }
    }
    document.querySelectorAll("[data-cart-trigger]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.openCart(undefined, btn);
      });
      btn.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        this.openCart(undefined, btn);
      });
    });

    this.closeBtn?.addEventListener("click", () => this.closeCart());

    document.addEventListener("cart:updated", () => {
      this.fetchCartSection();
    });

    this.clearBtn?.addEventListener("click", () => this.showClearCartConfirmation());
    this.confirmBtn?.addEventListener("click", () => this.clearCart());
    this.cancelBtn?.addEventListener("click", () => this.closeClearCartConfirmation());
    this.confirmationPopup?.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      this.closeClearCartConfirmation();
    });
    document.addEventListener("discount-updated", () => this.fetchCartSection());
    window.addEventListener("pageshow", () => {
      this.fetchCartSection();
    });
  }

  openCart(productId, opener) {
    if (productId) this.lastAddedProductId = productId;
    const wasOpen = this.drawer?.classList.contains("active");
    this.drawer?.classList.add("active");
    if (this.isDrawer && !wasOpen) {
      window.BzA11y?.trapFocus(this.drawer, {
        initial: this.closeBtn || undefined,
        returnTo: opener || document.activeElement,
      });
    }
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this.handleEsc);
    document.addEventListener("click", this.handleOutsideClick);
    this.fetchCartSection(this.lastAddedProductId);
  }

  closeCart() {
    const wasOpen = this.drawer?.classList.contains("active");
    this.drawer?.classList.remove("active");
    if (this.isDrawer && wasOpen) {
      this.closeClearCartConfirmation();
      window.BzA11y?.releaseFocus(this.drawer);
    }
    document.body.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", this.handleEsc);
    document.removeEventListener("click", this.handleOutsideClick);
  }

  handleEsc(e) {
    if (e.key !== "Escape") return;
    if (this.confirmationPopup?.classList.contains("visible")) return; // the confirmation closes first
    this.closeCart();
  }

  handleOutsideClick(e) {
    if (!this.drawer.contains(e.target) && !e.target.closest("[data-cart-trigger]")) {
      this.closeCart();
    }
  }

  showClearCartConfirmation() {
    if (this.clearLoader) {
      this.clearLoader.classList.remove("hidden");
    }
    setTimeout(() => {
      this.confirmationPopup.classList.add('visible');
      window.BzA11y?.trapFocus(this.confirmationPopup, {
        initial: this.cancelBtn || undefined,
        returnTo: this.clearBtn,
        delay: 20,
      });
    }, 400);
    setTimeout(() => {
      if (this.clearLoader) {
        this.clearLoader.classList.add("hidden");
      }
    }, 300);
  }
  closeClearCartConfirmation() {
    const wasVisible = this.confirmationPopup.classList.contains('visible');
    this.confirmationPopup.classList.remove('visible');
    if (wasVisible) window.BzA11y?.releaseFocus(this.confirmationPopup);
    if (this.clearLoader) {
      this.clearLoader.classList.add("hidden");
    }
  }
  handleClearCartConfirmation() {
    this.confirmationPopup.classList.remove('visible');
  }

  clearCart() {
    fetch("/cart/clear.js", { method: "POST", headers: { "Content-Type": "application/json" } })
      .then((res) => res.json())
      .then(() => this.fetchCartSection())
      .catch((err) => console.error("Error clearing cart:", err));
      this.closeClearCartConfirmation();
  }

  fetchCartSection(productId) {
    if (this._cartSectionAbort) this._cartSectionAbort.abort();
    this._cartSectionAbort = new AbortController();
    const { signal } = this._cartSectionAbort;
    const root = window.Shopify?.routes?.root || "/";

    const loadSection = () =>
      fetch(`${root}?section_id=cart-sidebar`, { signal, cache: "no-store" })
        .then((res) => res.text())
        .then((html) => this.renderCartHTML(html));

    const ignoreAbort = (err) => {
      if (err?.name !== "AbortError") console.error("Error fetching cart section:", err);
    };

    // Persist the just-added product so related items resolve correctly even when
    // re-adding the same product (cart.items order does not change on qty updates).
    if (productId) {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal,
        cache: "no-store",
        body: JSON.stringify({
          attributes: { _last_added_product_id: String(productId) },
        }),
      })
        .then((res) => res.json())
        .then((cart) => {
          this.updateCartCount(cart);
          return loadSection();
        })
        .catch(ignoreAbort);
      return;
    }

    loadSection().catch(ignoreAbort);
    fetch("/cart.js", { signal, cache: "no-store" })
      .then((res) => res.json())
      .then((cart) => this.updateCartCount(cart))
      .catch((err) => {
        if (err?.name !== "AbortError") console.error("Error fetching cart JSON:", err);
      });
  }
  updateCartCount(cart) {
    const countEls = document.querySelectorAll("[data-cart-count]");
    if (!countEls.length) return;
    const totalItems = cart.item_count || 0;
    countEls.forEach((el) => {
      el.textContent = totalItems;
    });
  }
  renderCartHTML(html) {
    if (!this.itemsContainer || !html) return;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const newItems = tempDiv.querySelector("[data-cart-items]");
    const newSubtotal = tempDiv.querySelector("[data-cart-subtotal]");
    const newRelated = tempDiv.querySelector("[data-cart-related]");
    const relatedEl = this.querySelector("[data-cart-related]");

    // Remember the focused control inside the items list so keyboard users keep their place
    const active = document.activeElement;
    let focusMemo = null;
    if (active && this.itemsContainer.contains(active)) {
      focusMemo = {
        key: active.closest("[data-key]")?.dataset.key,
        selector: active.matches("input") ? "input" : active.getAttribute("aria-label") ? `[aria-label="${CSS.escape(active.getAttribute("aria-label"))}"]` : active.className ? `.${[...active.classList].map((c) => CSS.escape(c)).join(".")}` : active.tagName.toLowerCase(),
      };
    }

    const newDrawer = tempDiv.querySelector("[data-cart-sidebar]");
    if (newDrawer && this.drawer) {
      this.drawer.classList.toggle("cart_blank", newDrawer.classList.contains("cart_blank"));
      const newBadge = newDrawer.querySelector(".bz-footer_badge");
      const oldBadge = this.drawer.querySelector(":scope > .bz-footer_badge");
      if (oldBadge && !newBadge) oldBadge.remove();
      else if (newBadge && !oldBadge) this.drawer.prepend(newBadge);
      else if (newBadge && oldBadge) oldBadge.replaceWith(newBadge);
    }
    const newTotals = tempDiv.querySelector(".cart_totals");
    const oldTotals = this.querySelector(".cart_totals");
    if (oldTotals && newTotals) oldTotals.replaceWith(newTotals);
    const newProgress = tempDiv.querySelector("[data-cart-progress]");
    const oldProgress = this.querySelector("[data-cart-progress]");
    if (oldProgress && newProgress) oldProgress.replaceWith(newProgress);
    else if (oldProgress && !newProgress) oldProgress.remove();
    else if (!oldProgress && newProgress) {
      const discountEl = this.querySelector("#cart-discount");
      discountEl?.after(newProgress);
    }
    this.subtotalEl = this.querySelector("[data-cart-subtotal]");

    if (newItems) this.itemsContainer.innerHTML = newItems.innerHTML;

    if (focusMemo) {
      const row = focusMemo.key ? this.itemsContainer.querySelector(`[data-key="${CSS.escape(focusMemo.key)}"]`) : null;
      const target = row?.querySelector(focusMemo.selector) || row?.querySelector("a[href], button, input");
      const fallback = window.BzA11y?.getFocusable(this.drawer)[0];
      (target || fallback)?.focus({ preventScroll: true });
    }
    if (newSubtotal && this.subtotalEl) this.subtotalEl.textContent = newSubtotal.textContent;
    if (relatedEl) relatedEl.innerHTML = newRelated ? newRelated.innerHTML : "";
    this.querySelectorAll(".remove-item").forEach((btn) => {
      btn.addEventListener("click", () => this.removeItem(btn.dataset.key, btn.dataset.giftChild));
    });
    const discountComponent = this.querySelector("cart-discount-wrap");
    if (discountComponent && typeof discountComponent.connectedCallback === "function") {
      discountComponent.connectedCallback();
    }
  }
  removeItem(key, childKey) {
    const endpoint = childKey ? "/cart/update.js" : "/cart/change.js";
    const body = childKey
      ? { updates: { [key]: 0, [childKey]: 0 } }
      : { id: key, quantity: 0 };
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then(() => this.fetchCartSection())
      .catch((err) => console.error("Error removing item:", err));
  }

}
customElements.define("bz-cart-sidebar", BzCartSidebar);

// CART RELATED PRODUCTS SLIDER ===========
// Re-created on every cart re-render (innerHTML swap), so it inits fresh in
// connectedCallback and tears down in disconnectedCallback to avoid leaking
// Swiper's window resize listeners.
if (!customElements.get("cart-related-slider")) {
  customElements.define("cart-related-slider", class extends HTMLElement {
    connectedCallback() {
      if (this.swiper || typeof Swiper === "undefined") return;
      // Defer one frame so the open drawer has a measurable width after AJAX swaps
      requestAnimationFrame(() => {
        if (this.swiper || !this.isConnected) return;
        this.swiper = new Swiper(this, {
          slidesPerView: 1.2,
          spaceBetween: 12,
          watchOverflow: true,
        });
        this.querySelector(".swiper-wrapper")?.classList.add("swiper-loaded");
      });
    }
    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  });
}

// CART DISCOUNT COMPONENT ===========
class CartDiscount extends HTMLElement {
  #activeFetch = null;

  connectedCallback() {
    this.cartDiscountError = this.querySelector('[data-ref="cartDiscountError"]');
    this.cartDiscountErrorDiscountCode = this.querySelector('[data-ref="cartDiscountErrorDiscountCode"]');
    this.cartDiscountErrorShipping = this.querySelector('[data-ref="cartDiscountErrorShipping"]');

    this.addEventListener('submit', this.applyDiscount);
    this.addEventListener('click', this.removeDiscount);
  }
  disconnectedCallback() {
    this.removeEventListener('submit', this.applyDiscount);
    this.removeEventListener('click', this.removeDiscount);
  }
  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const submitButton = form.querySelector('button.type_submit');
    submitButton?.setAttribute('disabled', 'true');

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    const sectionId = this.dataset.sectionId;
    const cartUpdateUrl = this.dataset.cartUpdateUrl;
    if (!sectionId || !cartUpdateUrl) return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      this.cartDiscountError.classList.add('hidden');
      this.cartDiscountErrorDiscountCode.classList.add('hidden');
      this.cartDiscountErrorShipping.classList.add('hidden');

      const bodyData = {
        discount: [...existingDiscounts, discountCodeValue].join(','),
        sections: [sectionId],
      };

      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
        signal: abortController.signal,
      });

      const data = await response.json();

      const isInvalid = data.discount_codes.find(discount => {
        return discount.code === discountCodeValue && discount.applicable === false;
      });

      if (isInvalid) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');

      const section = parsedHtml.getElementById(sectionId);
      const discountCodes = section?.querySelectorAll('.dis_pill') || [];

      if (section) {
        const codes = Array.from(discountCodes)
          .map(el => el instanceof HTMLLIElement ? el.dataset.discountCode : null)
          .filter(Boolean);

        const sameAsBefore = codes.length === existingDiscounts.length &&
          codes.every(code => existingDiscounts.includes(code));

        const foundValid = data.discount_codes.find(discount => {
          return discount.code === discountCodeValue && discount.applicable === true;
        });

        if (sameAsBefore && foundValid) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }

        const targetSection = document.getElementById(sectionId);
        if (targetSection && section.innerHTML) {
          targetSection.innerHTML = section.innerHTML;
          const newComponent = targetSection.querySelector('cart-discount-wrap');
          if (newComponent && typeof newComponent.connectedCallback === 'function') {
            newComponent.connectedCallback();
          }
        }
      }

      const discountEvent = new CustomEvent('discount-updated', {
        detail: { data, id: this.id },
      });
      document.dispatchEvent(discountEvent);

    } catch (error) {
      console.error('Discount apply error:', error);
    } finally {
      submitButton?.removeAttribute('disabled');
      this.#activeFetch = null;
    }
  };

  removeDiscount = async (event) => {
    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    const pill = event.target.closest('.dis_pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const sectionId = this.dataset.sectionId;
    const cartUpdateUrl = this.dataset.cartUpdateUrl;
    if (!sectionId || !cartUpdateUrl) return;

    const abortController = this.#createAbortController();

    try {
      const bodyData = {
        discount: existingDiscounts.join(','),
        sections: [sectionId],
      };

      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
        signal: abortController.signal,
      });

      const data = await response.json();

      const discountEvent = new CustomEvent('discount-updated', {
        detail: { data, id: this.id },
      });
      document.dispatchEvent(discountEvent);

      const newHtml = data.sections[sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');

      const section = parsedHtml.getElementById(sectionId);
      const targetSection = document.getElementById(sectionId);

      if (targetSection && section?.innerHTML) {
        targetSection.innerHTML = section.innerHTML;

        const newComponent = targetSection.querySelector('cart-discount-wrap');
        if (newComponent && typeof newComponent.connectedCallback === 'function') {
          newComponent.connectedCallback();
        }
      }

    } catch (error) {
      console.error('Discount remove error:', error);
    } finally {
      this.#activeFetch = null;
    }
  };

  #handleDiscountError(type) {
    this.cartDiscountError.classList.remove('hidden');

    if (type === 'discount_code') {
      this.cartDiscountErrorDiscountCode.classList.remove('hidden');
    } else {
      this.cartDiscountErrorShipping.classList.remove('hidden');
    }
  }

  #existingDiscounts() {
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.dis_pill');

    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}
if (!customElements.get('cart-discount-wrap')) {
  customElements.define('cart-discount-wrap', CartDiscount);
}

// Cart note ===========
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
}
if (!customElements.get('cart-note-wrap')) {
  customElements.define('cart-note-wrap', class extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('input', debounce(e => 
        fetch('/cart/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: e.target.value })
        })
      , 500));
    }
  });
}